import { zodResolver } from '@hookform/resolvers/zod';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { CompanyInformationSchema } from '@repo/shared/schema';
import { ReportAutofillStatus } from '@repo/shared/types';
import { Button } from '@repo/ui/button';
import { DateInput } from '@repo/ui/date-input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Separator } from '@repo/ui/separator';
import { TypewriterField } from '@repo/ui/typewriter-input';
import { Check, Loader2, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useParams, useRouteLoaderData } from 'react-router';
import { toast } from 'sonner';
import type { z } from 'zod';
import { useONavigate } from '@/hooks/use-organization-navigate';
import type { WizardLoaderData, WizardReport } from '@/lib/report';
import { trpc } from '@/providers/trpc';
import { useSetUnsavedChanges } from '@/providers/unsaved-changes';

const CompanyInformationFormSchema = CompanyInformationSchema;

type CompanyInformationForm = z.infer<typeof CompanyInformationFormSchema>;
type SaveState = 'idle' | 'saving' | 'saved';

export function CompanyInformationStep() {
  useLocale();

  const { reportId = '' } = useParams();
  const oNavigate = useONavigate();

  const { report: initialReport } = useRouteLoaderData('report-create-wizard') as WizardLoaderData;

  const { setUnsavedChanges, clearUnsavedChanges } = useSetUnsavedChanges();
  const hasMarkedUnsaved = useRef(false);

  const { data: report } = trpc.report.getReport.useQuery(
    { reportId },
    {
      initialData: initialReport as never,
      refetchInterval: (query) => {
        const data = query.state.data as WizardReport | undefined;
        return data?.autofillStatus === ReportAutofillStatus.PROCESSING ? 2500 : false;
      },
    },
  );

  const isAutofillRunning = report?.autofillStatus === ReportAutofillStatus.PROCESSING;

  const { mutateAsync: updateCompanyInfo } = trpc.report.updateCompanyInfo.useMutation({
    meta: { doNotInvalidateQueryOnMutation: true },
  });

  const { mutate: submitReport, isPending: isSubmitting } = trpc.report.submitReport.useMutation({
    onSuccess: () => {
      void clearUnsavedChanges();
      toast.success(t('reports.reportSubmittedSuccessfully'));
      oNavigate({ to: `/reports/${reportId}` });
    },
    onError: () => {
      toast.error(t('reports.failedToSubmitReport'), {
        get description() {
          return t('reports.pleaseCheckTheFormAndRetry');
        },
      });
    },
  });

  const ci = report?.companyInformation;

  const form = useForm<CompanyInformationForm>({
    resolver: zodResolver(CompanyInformationFormSchema, { error: validationError }),
    defaultValues: {
      companyName: ci?.companyName,
      countryOfIncorporation: ci?.countryOfIncorporation,
      ...ci,
    },
  });

  const { isDirty } = form.formState;

  useEffect(() => {
    if (!isDirty || hasMarkedUnsaved.current) return;
    hasMarkedUnsaved.current = true;
    setUnsavedChanges({
      get message() {
        return t('reports.youHaveEditedCompanyInformationLeavingNowMayLose');
      },
      get proceedLinkLabel() {
        return t('extra.leaveAnyway');
      },
    });
  }, [isDirty, setUnsavedChanges]);

  // Reset when autofill finishes
  useEffect(() => {
    if (!isAutofillRunning && report?.companyInformation) {
      const c = report.companyInformation;
      form.reset({ ...c });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutofillRunning]);

  // ── Autosave ──────────────────────────────────────────────────────────────
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const watchedValues = useWatch({ control: form.control });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      setSaveState('saving');
      try {
        await updateCompanyInfo({
          reportId,
          companyInformation: watchedValues as Partial<CompanyInformationForm>,
        });
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 2000);
      } catch {
        setSaveState('idle');
      }
    }, 600);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues]);

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-6"
        onSubmit={form.handleSubmit(() => submitReport({ reportId }))}
      >
        {/* Save indicator */}
        <div className="flex h-5 items-center justify-end">
          {saveState === 'saving' && (
            <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('reports.saving')}{' '}
            </span>
          )}
          {saveState === 'saved' && (
            <span className="flex items-center gap-1.5 text-green-600 text-xs">
              <Check className="h-3.5 w-3.5" /> {t('reports.saved')}{' '}
            </span>
          )}
        </div>

        {/* ── Company identity ── */}
        <Section title={t('reports.companyIdentity')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              label={t('reports.companyName2')}
              name="companyName"
            />
            <TextField
              control={form.control}
              label={t('reports.registrationNumber')}
              name="companyRegistrationNumber"
            />
            <TextField
              control={form.control}
              label={t('reports.companyIdNumber2')}
              name="companyIdNumber"
            />
            <TextField control={form.control} label={t('reports.taxId')} name="companyTaxId" />
            <TextField
              control={form.control}
              label={t('reports.countryOfIncorporation2')}
              name="countryOfIncorporation"
            />
            <TextField
              control={form.control}
              label={t('reports.incorporationDate')}
              name="incorporationDate"
              placeholder={t('reports.yyyyMmDd')}
            />
          </div>
        </Section>

        <Separator />

        {/* ── Business activity ── */}
        <Section title={t('reports.businessActivity')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField control={form.control} label={t('reports.sector')} name="sector" />
            <TextField control={form.control} label={t('reports.industry')} name="industry" />
            <div className="sm:col-span-2">
              <TextField
                control={form.control}
                label={t('reports.registeredAddress')}
                name="registeredAddress"
              />
            </div>
          </div>
        </Section>

        <Separator />

        {/* ── Financial ── */}
        <Section title={t('reports.financialProfile')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              label={t('reports.annualRevenue')}
              name="annualRevenue"
            />
            <TextField control={form.control} label={t('reports.netWorth')} name="netWorth" />
            <div className="sm:col-span-2">
              <TextField
                control={form.control}
                label={t('reports.ultimateBeneficialOwners')}
                name="ultimateBeneficialOwners"
              />
            </div>
            <div className="sm:col-span-2">
              <TextField control={form.control} label={t('reports.directors')} name="directors" />
            </div>
          </div>
        </Section>

        <Separator />

        {/* ── Contact ── */}
        <Section title={t('reports.contact')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              label={t('reports.contactEmail')}
              name="contactEmail"
              type="email"
            />
            <TextField
              control={form.control}
              label={t('reports.contactPhone')}
              name="contactPhone"
              type="tel"
            />
          </div>
        </Section>

        <div className="flex justify-end pt-2">
          <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
            <Send className="me-2 h-4 w-4" />
            {t('reports.submitReport')}{' '}
          </Button>
        </div>
      </form>
    </Form>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  useLocale();

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-foreground text-sm">{title}</h3>
      {children}
    </div>
  );
}

function TextField({
  control,
  name,
  label,
  placeholder,
  type = 'text',
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  name: string;
  label: string;
  placeholder?: string;
  type?: string;
}) {
  useLocale();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {name === 'dateOfBirth' || name === 'incorporationDate' ? (
              <DateInput {...field} placeholder={label} />
            ) : (
              <TypewriterField placeholder={placeholder ?? label} type={type} {...field} />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
